<template>
  <div class="address-manager">
    <div class="d-flex justify-content-between align-items-end mb-4 pb-2 border-bottom-dark">
      <div>
        <h2 class="h6 text-uppercase fw-bold mb-0 letter-spacing-2" style="font-size: 12px;">{{ t('account.addresses.title') }}</h2>
        <p class="small text-muted mb-0 mt-1" style="font-size: 9px; letter-spacing: 0.3px;">{{ t('account.addresses.subtitle') }}</p>
      </div>
      <button 
        v-if="!showForm" 
        @click="openAddForm" 
        class="btn btn-dark btn-sm px-4"
      >
        {{ t('account.addresses.add_new') }}
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border spinner-border-sm text-teal" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Address List -->
<div v-else-if="!showForm" class="address-list">
      <div v-if="addresses.length === 0" class="empty-state text-center py-5">
        <div class="empty-icon mb-3">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </div>
        <p class="mb-3 small text-muted text-uppercase letter-spacing-1">{{ t('account.addresses.no_addresses') }}</p>
        <button @click="openAddForm" class="btn btn-dark btn-sm px-4">{{ t('account.addresses.add_first') }}</button>
      </div>
      
      <div class="row g-3">
        <div v-for="address in addresses" :key="address.addressId" class="col-12 col-sm-6 col-lg-4">
          <div class="card h-100 address-card shadow-premium" :class="{ 'is-default': address.isDefault }">
            <div class="card-body p-4">
              <div class="d-flex justify-content-between align-items-start mb-3">
                <h5 class="card-title h6 mb-0 text-uppercase fw-bold letter-spacing-1">
                  {{ (address.firstName && address.firstName.trim()) || (address.lastName && address.lastName.trim()) ? `${address.firstName || ''} ${address.lastName || ''}`.trim() : t('account.addresses.title') }}
                </h5>
                <div class="d-flex flex-column align-items-end gap-1">
                  <span v-if="address.isDefault" class="badge badge-state badge-default">{{ t('account.addresses.default') }}</span>
                  <span class="badge badge-state badge-type">{{ address.addressType }}</span>
                </div>
              </div>
               
              <div class="address-details mb-4">
                <p v-if="address.companyName" class="mb-1 small fw-bold text-uppercase letter-spacing-1" style="font-size: 10px;">{{ address.companyName }}</p>
                <p class="mb-1">{{ address.street }}</p>
                <p class="mb-1">
                  {{ address.zipCode }} {{ address.city }}
                  <span v-if="address.stateProvince && address.stateProvince.trim() !== ''">({{ address.stateProvince }})</span>
                </p>
                <p class="mb-0">{{ address.country }}</p>
                <p v-if="address.phone" class="mb-0 mt-1 small text-muted">
                  <span class="text-uppercase letter-spacing-1 me-1">{{ t('account.addresses.phone_label') }}:</span>
                  {{ address.phone }}
                </p>
                <p v-if="address.notes" class="mt-3 small text-muted fst-italic border-start ps-2 border-teal-light">
                  "{{ address.notes }}"
                </p>
              </div>
               
              <div class="d-flex gap-3 pt-3 border-top border-light">
                <button @click="editAddress(address)" class="btn-icon-action" :aria-label="t('account.addresses.edit')">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="m13.498.795.149-.149a1.207 1.207 0 1 1 1.707 1.708l-.149.148a1.5 1.5 0 0 1-.059 2.059L4.854 14.854a.5.5 0 0 1-.233.131l-4 1a.5.5 0 0 1-.606-.606l1-4a.5.5 0 0 1 .131-.232l9.642-9.642a.5.5 0 0 0-.642.056L6.854 4.854a.5.5 0 1 1-.708-.708L9.44.854A1.5 1.5 0 0 1 11.5.796a1.5 1.5 0 0 1 1.998.001z"/>
                  </svg>
                </button>
                <button @click="confirmDelete(address.addressId)" class="btn-icon-action text-danger" :aria-label="t('account.addresses.delete')">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                    <path fill-rule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Form -->
    <div v-else class="address-form-container">
      <div class="card border-0 shadow-premium">
        <div class="card-body p-5">
          <h3 class="h6 text-uppercase fw-bold mb-4 letter-spacing-2 border-bottom pb-3">{{ isEditing ? t('account.addresses.edit_title') : t('account.addresses.add_title') }}</h3>
          
          <form @submit.prevent="saveAddress" class="premium-form">
            <div class="row g-4">
              <div class="col-md-12">
                <div class="form-group">
                  <label for="addressType" class="form-label-premium">Tipo Indirizzo</label>
                  <select id="addressType" name="addressType" v-model="form.addressType" class="form-select-premium" required>
                    <option value="Shipping">Shipping</option>
                    <option value="Home">Home</option>
                    <option value="Billing">Billing</option>
                    <option value="Office">Office</option>
                  </select>
                </div>
              </div>

              <div class="col-md-12">
                <div class="form-group">
                  <label for="companyName" class="form-label-premium">Azienda / Ragione Sociale (Opzionale)</label>
                  <input id="companyName" name="companyName" v-model="form.companyName" type="text" class="form-control" placeholder="es. ABC Srl">
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group">
                  <label for="firstName" class="form-label-premium">{{ t('account.personal_data.label.name') }}</label>
                  <input id="firstName" name="firstName" v-model="form.firstName" type="text" class="form-control" required>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label for="lastName" class="form-label-premium">{{ t('account.personal_data.label.surname') }}</label>
                  <input id="lastName" name="lastName" v-model="form.lastName" type="text" class="form-control" required>
                </div>
              </div>
              
              <div class="col-12">
                <div class="form-group">
                  <label for="street" class="form-label-premium">{{ t('account.personal_data.label.address') }}</label>
                  <input id="street" name="street" v-model="form.street" type="text" class="form-control" required>
                </div>
              </div>
              
              <div class="col-md-4">
                <div class="form-group">
                  <label for="zipCode" class="form-label-premium">{{ t('account.personal_data.label.postalCode') }}</label>
                  <input id="zipCode" name="zipCode" v-model="form.zipCode" type="text" class="form-control" required>
                </div>
              </div>
              <div class="col-md-8">
                <div class="form-group">
                  <label for="city" class="form-label-premium">{{ t('account.personal_data.label.city') }}</label>
                  <input id="city" name="city" v-model="form.city" type="text" class="form-control" required>
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="form-group">
                  <label for="stateProvince" class="form-label-premium">Provincia (Sigla)</label>
                  <input id="stateProvince" name="stateProvince" v-model="form.stateProvince" type="text" class="form-control" placeholder="es. MI" required>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label for="country" class="form-label-premium">{{ t('account.personal_data.label.country') }}</label>
                  <input id="country" name="country" v-model="form.country" type="text" class="form-control" required>
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group">
                  <label for="phone" class="form-label-premium">{{ t('account.addresses.phone') }}</label>
                  <input
                    id="phone"
                    name="phone"
                    v-model="form.phone"
                    type="tel"
                    class="form-control"
                    :placeholder="t('account.addresses.phone_placeholder')"
                    inputmode="tel"
                    autocomplete="tel"
                    pattern="^\+?[0-9\s-]{8,20}$"
                    maxlength="20"
                  >
                </div>
              </div>

              <div class="col-12">
                <div class="form-group">
                  <label for="notes" class="form-label-premium">Note Consegna</label>
                  <textarea id="notes" name="notes" v-model="form.notes" class="form-control" rows="2" placeholder="es. Citofonare Rossi..."></textarea>
                </div>
              </div>
              
              <div class="col-12">
                <div class="default-address-toggle" @click="form.isDefault = !form.isDefault">
                  <div class="toggle-switch" :class="{ 'is-active': form.isDefault }">
                    <div class="toggle-handle"></div>
                  </div>
                  <span class="toggle-label">{{ t('account.addresses.set_default') }}</span>
                </div>
              </div>
            </div>
            
            <div class="d-flex gap-3 mt-5 form-actions-mobile">
              <button type="submit" class="btn btn-dark px-5" :disabled="saving">
                <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                {{ t('account.personal_data.btn.save') }}
              </button>
              <button type="button" @click="cancelForm" class="btn btn-outline-secondary px-4" :disabled="saving">
                {{ t('account.personal_data.btn.cancel') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import apiClient from '@/services/api'
import { useLanguage } from '@/composables/useLanguage'
import { useToast } from '@/composables/useToast'

const { t } = useLanguage()
const toast = useToast()

const addresses = ref([])
const loading = ref(true)
const saving = ref(false)
const showForm = ref(false)
const isEditing = ref(false)

const initialForm = {
  addressId: null,
  firstName: '',
  lastName: '',
  addressType: 'Shipping',
  companyName: '',
  street: '',
  city: '',
  zipCode: '',
  stateProvince: '',
  country: 'Italy',
  phone: '',
  isDefault: false,
  notes: ''
}

const form = ref({ ...initialForm })

const fetchAddresses = async () => {
  loading.value = true
  try {
    const response = await apiClient.get('/CustomerAddresses')
    addresses.value = response.data.data || []
  } catch (error) {
    console.error('Error fetching addresses:', error)
    toast.error(t('toast.error.generic'))
  } finally {
    loading.value = false
  }
}

const openAddForm = () => {
  form.value = { ...initialForm }
  isEditing.value = false
  showForm.value = true
}

const editAddress = (address) => {
  form.value = { ...address }
  isEditing.value = true
  showForm.value = true
}

const cancelForm = () => {
  showForm.value = false
}

const saveAddress = async () => {
  saving.value = true
  try {
    const payload = { ...form.value, phone: (form.value.phone || '').trim() || null }
    if (isEditing.value) {
      await apiClient.put(`/CustomerAddresses/${payload.addressId}`, payload)
    } else {
      await apiClient.post('/CustomerAddresses', payload)
    }
    await fetchAddresses()
    showForm.value = false
    toast.success(t('toast.success.profile_update'))
  } catch (error) {
    console.error('Error saving address:', error)
    toast.error(t('toast.error.generic'))
  } finally {
    saving.value = false
  }
}

const confirmDelete = async (id) => {
  if (confirm(t('account.addresses.confirm_delete'))) {
    try {
      await apiClient.delete(`/CustomerAddresses/${id}`)
      await fetchAddresses()
      toast.success(t('toast.success.generic'))
    } catch (error) {
      console.error('Error deleting address:', error)
      toast.error(t('toast.error.generic'))
    }
  }
}

onMounted(() => {
  fetchAddresses()
})
</script>

<style scoped>
.letter-spacing-1 { letter-spacing: 1.5px; }
.letter-spacing-2 { letter-spacing: 2.5px; }

.text-teal { color: var(--teal) !important; }
.bg-light-teal { background-color: #f8fbfa; border: 1px dashed var(--light); }

.shadow-premium {
  box-shadow: 0 10px 30px rgba(0,0,0,0.03);
}

.border-bottom-dark {
  border-bottom: 1px solid #e4e4e4 !important;
}

/* Griglia responsive */
.address-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

/* Card */
.address-card {
  border: 1px solid #e4e4e4;
  border-radius: 0;
  background: #fff;
  transition: all 0.3s ease;
}

.address-card.is-default {
  border-top: 3px solid var(--teal);
}

.address-card:hover {
  box-shadow: 0 8px 24px rgba(0,0,0,0.06);
}

.card-body {
  padding: 20px;
}

@media (max-width: 576px) {
  .address-form-container .card-body {
    padding: 24px 16px !important;
  }
}

.card-title {
  font-size: 13px;
  font-weight: 700;
  color: #0d0d0b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Badge uniformi — stile tracking */
.badge {
  display: inline-block;
  padding: 4px 10px;
  font-size: 8px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-radius: 2px;
  border: 1px solid transparent;
  white-space: nowrap;
}

.badge-state {
  background: #f8f9fa;
  color: #37474f;
  border-color: #dee2e6;
}

.badge-default {
  background: #eaf7ef;
  color: #1f7a45;
  border-color: #c3e6cb;
}

.badge-type {
  background: #f8f9fa;
  color: #37474f;
  border-color: #dee2e6;
}

.address-details p {
  font-size: 13px;
  color: #555;
  margin-bottom: 5px;
}

/* Touch target minimo 40px */
.btn-icon-action {
  background: #f9f9f9;
  border: none;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #666;
  transition: all 0.2s ease;
  cursor: pointer;
}

.btn-icon-action:hover {
  background: #111;
  color: #fff;
  transform: scale(1.1);
}

.btn-icon-action.text-danger:hover {
  background: #dc3545;
  color: #fff;
}

.form-label-premium {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #999;
  margin-bottom: 8px;
  display: block;
}

.form-control, .form-select-premium, .form-select {
  border-radius: 0;
  border: 1px solid #e8e8e8;
  padding: 14px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.form-control:focus, .form-select:focus {
  border-color: #111;
  box-shadow: none;
  background-color: #fff;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #fafafa;
  border: 1px solid #e4e4e4;
}

.empty-icon {
  color: #bbb;
  margin-bottom: 16px;
}

.empty-state .btn {
  margin-top: 8px;
}

/* Modern Toggle Switch Style */
.default-address-toggle {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px 0;
  cursor: pointer;
  user-select: none;
  border-top: 1px solid #f0f0f0;
  margin-top: 10px;
}

.toggle-switch {
  width: 40px;
  height: 20px;
  background-color: #e0e0e0;
  border-radius: 20px;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toggle-switch.is-active {
  background-color: #5C8889;
}

.toggle-handle {
  width: 14px;
  height: 14px;
  background-color: #fff;
  border-radius: 50%;
  position: absolute;
  top: 3px;
  left: 3px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.is-active .toggle-handle {
  left: 23px;
}

.toggle-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #111;
}

@media (max-width: 480px) {
  .form-actions-mobile {
    flex-direction: column;
    gap: 12px !important;
  }
  .form-actions-mobile button {
    width: 100%;
  }
}

.default-address-toggle:hover .toggle-switch {
  opacity: 0.8;
}
</style>
